// YouTube mode — API contract, SWR keys + hooks, rating labels, formatters.
//
// Every URL used by a hook AND by a mutate() call comes from `ytKeys`, so the
// caches stay in sync. (A typo in one literal would silently stall rate mode:
// the queue would never revalidate and the selection would never advance.)
//
// Isolation: this file imports only the generic fetch wrapper from ../api;
// nothing in ../api knows about it.

import useSWR, { type SWRResponse } from "swr";

import { apiJson } from "../api/client";

export type YtListName = "queue" | "watched" | "ignore";
export const YT_LIST_NAMES: readonly YtListName[] = ["queue", "watched", "ignore"];

/** GET /api/youtube/lists */
export type YtListCounts = Record<YtListName, { count: number }>;

/** One row of GET /api/youtube/lists/{name} */
export interface YtListItem {
  video_id: string;
  title: string;
  channel: string | null;
  duration_seconds: number | null;
  published_at: string | null;
  watch_count: number;
  last_watched_at: string | null;
  unavailable: boolean;
  rating: number | null;
  listed_at: string | null;
}

export interface YtListItemsResponse {
  items: YtListItem[];
}

export interface YtDomain {
  domain: string;
  subdomain: string | null;
}

/** GET /api/youtube/videos/{id} + rate/remove responses */
export interface YtVideoDetail {
  video_id: string;
  title: string;
  channel: string | null;
  channel_url: string | null;
  url: string;
  description: string | null;
  published_at: string | null;
  duration_seconds: number | null;
  category: string | null;
  language: string | null;
  view_count: number | null;
  like_count: number | null;
  thumbnail_url: string | null;
  unavailable: boolean;
  watch_count: number;
  first_watched_at: string | null;
  last_watched_at: string | null;
  domains: YtDomain[];
  rating: number | null;
  list: YtListName | null;
}

/** GET/PUT /api/youtube/access */
export interface YtAccess {
  eligible: boolean;
  enabled: boolean;
  source_available: boolean;
}

/** POST /api/youtube/fill */
export interface YtFillResult {
  added: number;
  queue_total: number;
  batch_seq: number | null;
}

/** One row of GET /api/youtube/stats — same shape as the cinematic QueueStat. */
export interface YtQueueStat {
  seq: number;
  created_at: string;
  size: number;
  pending: number;
  complete: boolean;
  watched: number;
  unrated: number;
  rating_counts: Record<number, number>;
  avg_rating: number | null;
}

export interface YtQueueStatsResponse {
  queues: YtQueueStat[];
}

export const ytKeys = {
  access: "/api/youtube/access",
  lists: "/api/youtube/lists",
  list: (name: YtListName) => `/api/youtube/lists/${name}`,
  video: (id: string) => `/api/youtube/videos/${id}`,
  rate: (id: string) => `/api/youtube/videos/${id}/rate`,
  remove: (id: string) => `/api/youtube/videos/${id}/remove`,
  fill: "/api/youtube/fill",
  stats: "/api/youtube/stats",
  exportCsv: "/api/youtube/export/csv",
} as const;

function fetcher<T>(url: string): Promise<T> {
  return apiJson<T>(url);
}

/** Eligibility + enabled flag. Shared by the header button, the settings
 * section, and the YouTube shell; 200 for every signed-in user. */
export function useYtAccess(): SWRResponse<YtAccess, Error> {
  return useSWR<YtAccess>(ytKeys.access, fetcher<YtAccess>, { revalidateOnFocus: false });
}

export function useYtLists(): SWRResponse<YtListCounts, Error> {
  return useSWR<YtListCounts>(ytKeys.lists, fetcher<YtListCounts>);
}

/** Items of one list. Pass `null` to skip (collapsed tree node). */
export function useYtListItems(name: YtListName | null): SWRResponse<YtListItemsResponse, Error> {
  return useSWR<YtListItemsResponse>(
    name == null ? null : ytKeys.list(name),
    fetcher<YtListItemsResponse>,
  );
}

/** Full video detail. Pass `null` to skip (nothing selected). */
export function useYtVideo(videoId: string | null): SWRResponse<YtVideoDetail, Error> {
  return useSWR<YtVideoDetail>(
    videoId == null ? null : ytKeys.video(videoId),
    fetcher<YtVideoDetail>,
  );
}

export function useYtStats(): SWRResponse<YtQueueStatsResponse, Error> {
  return useSWR<YtQueueStatsResponse>(ytKeys.stats, fetcher<YtQueueStatsResponse>);
}

/** POST /fill — synchronous on the server (a random sample is milliseconds).
 * Throws ApiError; 409 = queue already full, 503 = corpus missing. */
export function fillQueue(): Promise<YtFillResult> {
  return apiJson<YtFillResult>(ytKeys.fill, { method: "POST" });
}

/** Relevance scale 1–9 → label. 0 is "not relevant" → ignore. */
export const YT_RATING_LABELS: Record<number, string> = {
  1: "noise",
  2: "filler",
  3: "fine",
  4: "useful",
  5: "valuable",
  6: "reference",
  7: "core",
  8: "—",
  9: "essential",
};

export const YT_ZERO_LABEL = "not relevant";

// ── autoplay preference ─────────────────────────────────────────────────────
// Per browser (localStorage), like theme / layout / sort — not server state.
// On by default: the workflow is "select a video, judge it, rate it, next".
// Browsers may still block autoplay WITH SOUND until the page has enough user
// interaction; YouTube then shows its play button, which is a harmless
// fallback. Never auto-mute — a muted video cannot be judged.

const AUTOPLAY_KEY = "cinedb.youtube.autoplay";

export function loadYtAutoplay(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage.getItem(AUTOPLAY_KEY) !== "off";
  } catch {
    return true;
  }
}

export function saveYtAutoplay(on: boolean): void {
  try {
    window.localStorage.setItem(AUTOPLAY_KEY, on ? "on" : "off");
  } catch {
    /* ignore */
  }
}

// ── formatters ──────────────────────────────────────────────────────────────

/** 754 → "12:34", 11132 → "3:05:32". */
export function fmtDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || !Number.isFinite(seconds)) return null;
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, "0") : String(m);
  return `${h > 0 ? `${h}:` : ""}${mm}:${String(sec).padStart(2, "0")}`;
}

export function fmtDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "2-digit", day: "2-digit" });
}

export function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function fmtCount(n: number | null | undefined): string | null {
  if (n == null) return null;
  return n.toLocaleString();
}
